import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { sendEmail } from '../utils/emailService';
import { useLanguage } from '../utils/i18n';
interface OrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName?: string;
}
interface FormState {
  name: string;
  phone: string;
  comment: string;
}
interface FormErrors {
  name?: string;
  phone?: string;
}
export function OrderModal({
  isOpen,
  onClose,
  productName
}: OrderModalProps) {
  const {
    t
  } = useLanguage();
  const [form, setForm] = useState<FormState>({
    name: '',
    phone: '',
    comment: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [loading, setLoading] = useState(false);
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.name.trim()) newErrors.name = t('order_err_name');
    if (!form.phone.trim()) {
      newErrors.phone = t('order_err_phone');
    } else if (!/^[\d\s\+\-\(\)]{7,}$/.test(form.phone.trim())) {
      newErrors.phone = t('order_err_phone_invalid');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const result = await sendEmail({
      from_name: form.name,
      phone: form.phone,
      message: form.comment,
      product: productName,
      form_type: 'Заказ товара'
    });
    setLoading(false);
    setStatus(result.ok ? 'success' : 'error');
  };
  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setForm({
        name: '',
        phone: '',
        comment: ''
      });
      setErrors({});
      setStatus('idle');
    }, 300);
  };
  return createPortal(<AnimatePresence data-id="element-315">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleClose} className="fixed inset-0 bg-black/60 z-[9998] backdrop-blur-sm" data-id="element-316" />
        
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none" data-id="element-317">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="w-full max-w-md bg-white rounded-lg shadow-2xl overflow-hidden pointer-events-auto" data-id="element-318">
            
              <div className="bg-red-600 p-6 text-white flex justify-between items-start" data-id="element-319">
                <div data-id="element-320">
                  <h3 className="text-xl font-heading font-bold" data-id="element-321">
                    {t('order_title')}
                  </h3>
                  {productName && <p className="text-red-100 text-sm mt-1" data-id="element-322">
                      {t('order_product')} {productName}
                    </p>}
                </div>
                <button onClick={handleClose} className="text-white/80 hover:text-white transition-colors" data-id="element-323">
                
                  <X className="h-5 w-5" data-id="element-324" />
                </button>
              </div>

              {status === 'success' ? <div className="p-8 text-center" data-id="element-325">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-326">
                    <CheckCircle className="h-8 w-8 text-green-600" data-id="element-327" />
                  </div>
                  <h4 className="font-heading text-xl font-bold text-gray-900 mb-2" data-id="element-328">
                    {t('contact_form_success')}
                  </h4>
                  <p className="text-gray-600 text-sm mb-6" data-id="element-329">
                    {t('order_success_desc')}
                  </p>
                  <Button onClick={handleClose} className="w-full" data-id="element-330">
                    {t('order_btn_close')}
                  </Button>
                </div> : <form onSubmit={handleSubmit} className="p-6 space-y-4" noValidate data-id="element-331">
              
                  <div data-id="element-332">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-333">
                      {t('contact_form_name')}{' '}
                      <span className="text-red-500" data-id="element-334">*</span>
                    </label>
                    <input name="name" type="text" value={form.name} onChange={handleChange} className={`w-full px-4 py-2.5 border rounded-sm focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all ${errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200'}`} placeholder={t('contact_name_placeholder')} data-id="element-335" />
                
                    {errors.name && <p className="mt-1 text-xs text-red-600 flex items-center gap-1" data-id="element-336">
                        <AlertCircle className="h-3 w-3" data-id="element-337" />
                        {errors.name}
                      </p>}
                  </div>

                  <div data-id="element-338">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-339">
                      {t('contact_form_phone')}{' '}
                      <span className="text-red-500" data-id="element-340">*</span>
                    </label>
                    <input name="phone" type="tel" value={form.phone} onChange={handleChange} className={`w-full px-4 py-2.5 border rounded-sm focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all ${errors.phone ? 'border-red-400 bg-red-50' : 'border-gray-200'}`} placeholder="+996 (___) ___-__-__" data-id="element-341" />
                
                    {errors.phone && <p className="mt-1 text-xs text-red-600 flex items-center gap-1" data-id="element-342">
                        <AlertCircle className="h-3 w-3" data-id="element-343" />
                        {errors.phone}
                      </p>}
                  </div>

                  <div data-id="element-344">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-345">
                      {t('order_qty_comment')}
                    </label>
                    <textarea name="comment" rows={3} value={form.comment} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none resize-none transition-all" placeholder={t('order_comment_placeholder')} data-id="element-346" />
                
                  </div>

                  <Button type="submit" className="w-full" isLoading={loading} data-id="element-347">
                    {t('contact_form_submit')}
                  </Button>
                  <p className="text-xs text-center text-gray-500" data-id="element-348">
                    {t('contact_form_policy')}
                  </p>
                </form>}
            </motion.div>
          </div>
        </>}
    </AnimatePresence>, document.body);
}